import { LitElement, css, html } from 'lit'

import {
  getProgramTerms,
} from '../../services/program-terms.js'
import {
  formatCurrentPrice,
} from '../../utils/format-price.js'
import {
  getProgramSummary,
} from '../../utils/get-program-summary.js'
import {
  formatTermCount,
} from '../../utils/polish-plurals.js'
import {
  findProgramComponent,
  scrollToProgramTerms,
} from '../../utils/program-navigation.js'

/**
 * Sticky pasek programu.
 *
 * Pobiera ten sam program i te same widoczne terminy co ct-offer-terms.
 * W WebWave wystarczy osadzić jeden element <ct-sticky-bar> — cały wygląd
 * i działanie znajdują się w tym pliku.
 */
export class CtStickyBar extends LitElement {
  static properties = {
    programKey: {
      type: String,
      attribute: 'program-key',
    },
    showAfter: {
      type: String,
      attribute: 'show-after',
    },
    hideAt: {
      type: String,
      attribute: 'hide-at',
    },
    targetId: {
      type: String,
      attribute: 'target-id',
    },
    program: { state: true },
    terms: { state: true },
    loading: { state: true },
  }

  constructor() {
    super()

    this.programKey = ''
    this.showAfter = 'ct-sticky-start'
    this.hideAt = 'ct-sticky-end'
    this.targetId = 'ct-terms-start'
    this.program = null
    this.terms = []
    this.loading = false

    this.visibilityReady = false
    this.visibilityFrame = null
    this.markerRetryTimer = null
    this.hasMovedToBody = false

    this.requestVisibilityUpdate =
      this.requestVisibilityUpdate.bind(this)
  }

  static styles = css`
    /* =====================================================
       1. USTAWIENIA WSPÓLNE

      Tutaj można szybko zmienić szerokość, kolory,
       wysokość paska na PC oraz jego cień.
    ===================================================== */
    :host {
      --ct-content-width: 1200px;
      --ct-desktop-height: 60px;
      --ct-orange: #f7931e;
      --ct-orange-hover: #ffac4d;
      --ct-green: #4a9e57;
      --ct-title-color: #fbc88a;
      --ct-text-color: #404040;
      --ct-divider-color: #e2e2e2;

      position: fixed;
      z-index: 99999;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      padding-bottom: env(safe-area-inset-bottom, 0px);
      background: #ffffff;
      box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.1);
      font-family: Poppins, Arial, sans-serif;
      opacity: 0;
      visibility: hidden;
      transform: translateY(100%);
      pointer-events: none;
      transition:
        opacity 0.25s ease,
        transform 0.25s ease,
        visibility 0.25s ease;
    }

    :host(.ct-sticky-offer--visible) {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      pointer-events: auto;
    }

    /* W edytorze WebWave pasek pozostaje zwykłym, widocznym elementem. */
    :host(.ct-sticky-offer--editor-preview) {
      position: relative;
      opacity: 1;
      visibility: visible;
      transform: none;
      pointer-events: auto;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    /* =====================================================
       2. GŁÓWNY UKŁAD ZAWARTOŚCI
    ===================================================== */
    .content {
      display: flex;
      width: 100%;
      max-width: var(--ct-content-width);
      min-height: var(--ct-desktop-height);
      margin: 0 auto;
      align-items: center;
      padding: 6px 8px;
    }

    /* =====================================================
       3. NAZWA PROGRAMU
    ===================================================== */
    .description {
      display: flex;
      flex: 1;
      min-width: 0;
      align-items: center;
      padding-right: 24px;
      padding-left: 16px;
      border-left: 4px solid var(--ct-orange);
    }

    .title,
    .subtitle {
      font-size: 22px;
      line-height: 1.35;
      white-space: nowrap;
    }

    .title {
      flex-shrink: 0;
      color: var(--ct-title-color);
      font-weight: 500;
    }

    .subtitle {
      min-width: 0;
      margin-left: 5px;
      overflow: hidden;
      color: var(--ct-text-color);
      font-weight: 400;
      text-overflow: ellipsis;
    }

    /* =====================================================
       4. CENA I LICZBA TERMINÓW
    ===================================================== */
    .details {
      flex: 0 0 210px;
      padding: 3px 24px;
      border-right: 1px solid var(--ct-divider-color);
      border-left: 1px solid var(--ct-divider-color);
    }

    .price {
      color: var(--ct-green);
      font-size: 20px;
      font-weight: 500;
      line-height: 1.3;
      white-space: nowrap;
    }

    .dates {
      margin-top: 1px;
      color: var(--ct-text-color);
      font-size: 16px;
      font-weight: 400;
      line-height: 1.3;
      white-space: nowrap;
    }

    /* =====================================================
       5. CTA
    ===================================================== */
    .cta {
      border: 0;
      cursor: pointer;
      font-family: inherit;
      display: flex;
      flex: 0 0 250px;
      height: 46px;
      margin-left: 24px;
      align-items: center;
      justify-content: center;
      border-radius: 9px;
      background: var(--ct-orange);
      color: #ffffff;
      font-size: 20px;
      font-weight: 500;
      line-height: 1;
      text-decoration: none;
      white-space: nowrap;
      transition: background-color 0.2s ease;
    }

    .cta:hover {
      background: var(--ct-orange-hover);
    }

    .cta:focus-visible {
      outline: 3px solid rgba(247, 147, 30, 0.4);
      outline-offset: 3px;
    }

    /* Dokładna wysokość paska na PC. */
    @media (min-width: 1200px) {
      .content {
        height: var(--ct-desktop-height);
        min-height: 0;
      }
    }

    /* =====================================================
       6. TABLET: 769–1199px
    ===================================================== */
    @media (min-width: 769px) and (max-width: 1199px) {
      .content {
        min-height: clamp(52px, 6.5vw, 56px);
        padding: 4px clamp(8px, 1.3vw, 12px);
      }

      .description {
        flex-direction: row;
        align-items: center;
        padding-right: clamp(10px, 1.6vw, 16px);
        padding-left: clamp(10px, 1.6vw, 16px);
        border-left-width: 3px;
      }

      .title,
      .subtitle {
        font-size: clamp(15px, 2vw, 18px);
        line-height: 1.3;
        white-space: nowrap;
      }

      .subtitle {
        margin-top: 0;
        margin-left: 5px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .details {
        flex-basis: clamp(160px, 20vw, 190px);
        padding-top: 0;
        padding-right: clamp(12px, 2vw, 20px);
        padding-bottom: 0;
        padding-left: clamp(12px, 2vw, 20px);
      }

      .price {
        font-size: clamp(16px, 2.2vw, 19px);
        line-height: 1.15;
      }

      .dates {
        margin-top: 0;
        font-size: clamp(13px, 1.8vw, 16px);
        line-height: 1.15;
      }

      .cta {
        flex-basis: clamp(180px, 24vw, 220px);
        height: 40px;
        margin-left: clamp(12px, 2vw, 20px);
        font-size: clamp(16px, 2.2vw, 19px);
      }
    }

    /* =====================================================
       7. TELEFON PIONOWO I POZIOMO: do 768px
    ===================================================== */
    @media (max-width: 768px) {
      .content {
        min-height: 0;
        padding: 5px clamp(12px, 3.5vw, 16px);
        gap: clamp(8px, 2.35vw, 12px);
      }

      .description {
        display: none;
      }

      .details {
        display: flex;
        flex: 1 1 47%;
        height: clamp(38px, calc(11.5vw - 2px), 42px);
        min-width: 0;
        flex-direction: column;
        justify-content: center;
        border: 0;
        border-left: 3px solid var(--ct-orange);
        padding: 0 0 0 clamp(8px, 2vw, 10px);
      }

      .price {
        font-size: 20px;
        line-height: 1.1;
      }

      .dates {
        margin-top: 2px;
        font-size: 13px;
        line-height: 1.15;
      }

      .cta {
        flex: 1 1 53%;
        min-width: 0;
        height: clamp(38px, calc(11.5vw - 2px), 42px);
        margin-left: 0;
        padding: 0 clamp(8px, 2.5vw, 14px);
        border-radius: clamp(7px, 2vw, 9px);
        font-size: 17px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      :host,
      .cta {
        transition: none;
      }
    }
  `

  connectedCallback() {
    super.connectedCallback()

    queueMicrotask(() => {
      if (this.isConnected) {
        this.setupPlacementAndVisibility()
      }
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.stopVisibilityTracking()
  }

  updated(changedProperties) {
    if (changedProperties.has('programKey')) {
      this.loadProgram()
    }

    if (
      changedProperties.has('showAfter') ||
      changedProperties.has('hideAt') ||
      changedProperties.has('programKey')
    ) {
      this.restartVisibilityTracking()
    }
  }

  async loadProgram() {
    this.loading = true

    try {
      const result = await getProgramTerms(
        this.programKey
      )

      this.program = result.program
      this.terms = result.terms
    } catch (error) {
      console.error(
        'Sticky pasek: nie udało się pobrać programu.',
        error
      )
      this.program = null
      this.terms = []
    } finally {
      this.loading = false
      this.requestVisibilityUpdate()
    }
  }

  setupPlacementAndVisibility() {
    /* W ramce edytora pasek pozostaje w bloku WebWave i jest zawsze widoczny. */
    if (window.self !== window.top) {
      this.classList.add(
        'ct-sticky-offer--editor-preview'
      )
      this.setAttribute('aria-hidden', 'false')
      return
    }

    /* Chroni pasek przed przycinaniem przez kontenery WebWave. */
    if (
      !this.hasMovedToBody &&
      document.body &&
      this.parentElement !== document.body
    ) {
      this.hasMovedToBody = true
      document.body.appendChild(this)
      return
    }

    this.restartVisibilityTracking()
  }

  restartVisibilityTracking() {
    if (!this.isConnected || window.self !== window.top) {
      return
    }

    this.stopVisibilityTracking()
    this.findMarkers(0)
  }

  findMarkers(attempt) {
    const startElement = document.getElementById(
      this.showAfter
    ) ?? findProgramComponent('ct-offer-summary', this.programKey)
    const endElement = document.getElementById(
      this.hideAt
    ) ?? findProgramComponent('ct-offer-terms', this.programKey)

    if (!startElement || !endElement) {
      if (attempt < 20) {
        this.markerRetryTimer = window.setTimeout(
          () => this.findMarkers(attempt + 1),
          500
        )
      } else {
        console.warn(
          'Sticky pasek: nie znaleziono elementu początkowego lub końcowego.'
        )
      }

      return
    }

    this.startElement = startElement
    this.endElement = endElement
    this.visibilityReady = true

    // API oraz rozwijane szczegóły zmieniają układ również bez scrollowania.
    this.markerResizeObserver = new ResizeObserver(this.requestVisibilityUpdate)
    this.markerResizeObserver.observe(startElement)
    this.markerResizeObserver.observe(endElement)

    window.addEventListener(
      'scroll',
      this.requestVisibilityUpdate,
      { passive: true }
    )
    window.addEventListener(
      'resize',
      this.requestVisibilityUpdate
    )

    this.requestVisibilityUpdate()
  }

  stopVisibilityTracking() {
    window.clearTimeout(this.markerRetryTimer)
    this.markerResizeObserver?.disconnect()
    this.markerResizeObserver = null

    if (this.visibilityFrame !== null) {
      window.cancelAnimationFrame(
        this.visibilityFrame
      )
    }

    window.removeEventListener(
      'scroll',
      this.requestVisibilityUpdate
    )
    window.removeEventListener(
      'resize',
      this.requestVisibilityUpdate
    )

    this.markerRetryTimer = null
    this.visibilityFrame = null
    this.visibilityReady = false
  }

  requestVisibilityUpdate() {
    if (
      !this.visibilityReady ||
      this.visibilityFrame !== null
    ) {
      return
    }

    this.visibilityFrame =
      window.requestAnimationFrame(() => {
        this.visibilityFrame = null
        this.updateVisibility()
      })
  }

  updateVisibility() {
    if (!this.startElement || !this.endElement) {
      return
    }

    // Hosty udostępniają prawdziwe CTA, które znajdują się w Shadow DOM.
    // Zewnętrzne znaczniki nadal działają dla starszych osadzeń.
    const startCta = this.startElement.getStickyStartElement
      ? this.startElement.getStickyStartElement()
      : this.startElement
    const endCta = this.endElement.getStickyEndElement
      ? this.endElement.getStickyEndElement()
      : this.endElement
    const startPosition = startCta?.getBoundingClientRect()
    const endPosition = endCta?.getBoundingClientRect()
    const startHasPassed = Boolean(startPosition && startPosition.bottom <= 0)
    // Warunek pozostaje prawdziwy również po minięciu kart. Przy powrocie
    // w górę pasek wróci dopiero, gdy pierwsze CTA wyjdzie dołem z ekranu.
    const endHasBeenReached = !endPosition ||
      endPosition.top <= window.innerHeight
    const hasTerms = this.terms.length > 0
    const shouldShow =
      startHasPassed &&
      !endHasBeenReached &&
      hasTerms &&
      !this.loading

    this.classList.toggle(
      'ct-sticky-offer--visible',
      shouldShow
    )
    this.setAttribute(
      'aria-hidden',
      String(!shouldShow)
    )
  }

  handleCtaClick(event) {
    scrollToProgramTerms(event, this.targetId, this.programKey)
  }

  render() {
    const usesOfferSummary = typeof document !== 'undefined' &&
      Boolean(findProgramComponent('ct-offer-summary', this.programKey))
    const summary = getProgramSummary(this.terms)
    const formattedPrice = formatCurrentPrice(
      summary.lowestPrice
    )
    const priceLabel = formattedPrice
      ? `${summary.pricesDiffer ? 'od ' : ''}${formattedPrice}`
      : 'Sprawdź cenę'
    const title =
      this.program?.stickyTitle ??
      this.program?.title ??
      ''
    const subtitle =
      this.program?.stickySubtitle ?? ''

    return html`
      <aside
        class="content"
        aria-label="Szybki wybór terminu"
      >
        <div class="description">
          <span class="title">${title}</span>
          ${subtitle
            ? html`<span class="subtitle">${subtitle}</span>`
            : ''}
        </div>

        <div class="details">
          <div class="price">${priceLabel}</div>
          <div class="dates">
            ${formatTermCount(summary.termCount)}
          </div>
        </div>

        ${usesOfferSummary ? html`
          <button class="cta" type="button" @click=${this.handleCtaClick}>
            WYBIERZ TERMIN
          </button>
        ` : html`
          <a class="cta" href="#${this.targetId}" @click=${this.handleCtaClick}>
            WYBIERZ TERMIN
          </a>
        `}
      </aside>
    `
  }
}

if (!customElements.get('ct-sticky-bar')) {
  customElements.define(
    'ct-sticky-bar',
    CtStickyBar
  )
}
